import AddIcon from "@mui/icons-material/Add";
import ChatIcon from "@mui/icons-material/Chat";
import { Box, Grid, Paper, useTheme } from "@mui/material";
import { alpha } from "@mui/system";
import ButtonBase from "@mui/material/ButtonBase";
import Grow from "@mui/material/Grow";
import Typography from "@mui/material/Typography";
import Cookies from "js-cookie";
import { FC, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { useAlert } from "../../../context/AlertContext";
import { useCustomNavigate } from "../../../hooks/useCustomNavigate";
import { useChatNavigate } from "../../../hooks/useChatNavigate";
import useSubscription from "../../../hooks/useSubscription";
import { apiRequest } from "../../../utils/api";
import { localDate } from "../../../utils/utils";
import BrowsingDialogs from "../../../components/dialogs/BrowsingDialogs";

interface Chats {
  uuid: string;
  name: string;
  latest_history: string;
  updated_at: string;
}

interface ChatLimitsResponse {
  success: boolean;
  is_free: boolean;
  max_chats: number;
  current_count: number;
  latest_chat_uuid: string | null;
}

interface BrowsingProps {
  data?: {
    chats: Chats[];
  };
  getData: () => Promise<void>;
}

const Browsing: FC<BrowsingProps> = ({ data, getData }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useCustomNavigate();
  const { setAlert } = useAlert();
  // 未使用のaddNewChatを削除
  const uiLanguage = Cookies.get("ui_language") || "en";

  const { createAndNavigateToNewChat } = useChatNavigate();
  const { isFree } = useSubscription();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [upgradeDialog, setUpgradeDialog] = useState(false);
  // chatLimitsのみを削除し、setChatLimitsは保持
  const [, setChatLimits] = useState<ChatLimitsResponse | null>(null);

  const handleCloseDialog = () => {
    setOpenDeleteDialog(false);
    setUpgradeDialog(false);
  };

  const handleConfirmDelete = async () => {
    setOpenDeleteDialog(false);
    // カスタムフックを使用して新規チャット作成
    try {
      const success = await createAndNavigateToNewChat();
      if (success) {
        await getData();
        setAlert("success", t("browsing.createChat"));
      }
    } catch (error) {
      console.error("Error creating chat with force delete:", error);
      setAlert("error", t("common.error"));
    }
  };

  const handleUpgrade = () => {
    setUpgradeDialog(false);
    navigate("/pricing");
  };

  const handleCreateNewChat = async () => {
    setUpgradeDialog(false);
    // カスタムフックを使用して新規チャット作成
    try {
      const success = await createAndNavigateToNewChat();
      if (success) {
        await getData();
        setAlert("success", t("browsing.createChat"));
      }
    } catch (error) {
      console.error("Error creating chat with force delete:", error);
      setAlert("error", t("common.error"));
    }
  };

  // チャット制限情報を取得する
  const getChatLimits = async (): Promise<ChatLimitsResponse | null> => {
    try {
      const res = await apiRequest("/api/v1/chat/limits", "GET");
      return res.data as ChatLimitsResponse;
    } catch (error) {
      console.error("Failed to fetch chat limits:", error);
      return null;
    }
  };

  // コンポーネントマウント時にチャット制限情報を取得
  useEffect(() => {
    getChatLimits().then(setChatLimits);
  }, []);

  // チャット作成エラーハンドリング
  const handleChatCreationError = (error: unknown) => {
    const axiosError = error as AxiosError;
    // APIからのエラーレスポンスを処理
    if (axiosError.response && axiosError.response.data) {
      const errorData = axiosError.response.data as {
        success: boolean;
        is_free: boolean;
        message: string;
      };

      // チャット数制限エラーの処理
      if (errorData.message === "chat_limit_exceeded") {
        // レスポンスのis_free情報を確認
        const responseIsFree = !!errorData.is_free;

        // フリープランかどうかで処理を分岐
        if (isFree || responseIsFree) {
          // フリープランユーザーの場合はアップグレードダイアログを表示
          setUpgradeDialog(true);
        } else {
          // 有料プランユーザーの場合は古いチャットを削除する確認ダイアログを表示
          setOpenDeleteDialog(true);
        }
        return;
      }
    }

    // その他のエラー
    setAlert("error", t("common.error"));
  };

  // 新しいチャット作成処理（チャット数制限を考慮）
  const executeNewChatCreation = async () => {
    try {
      const success = await createAndNavigateToNewChat();
      if (success) {
        getData().then(() => {
          setAlert("success", t("browsing.createChat"));
        });
      }
    } catch (error) {
      // エラーハンドリング関数を呼び出し
      handleChatCreationError(error);
    }
  };

  const handleAddChat = async () => {
    try {
      // チャット制限情報を取得
      const limits = await getChatLimits();

      if (!limits) {
        // 制限情報が取得できなかった場合は従来の方法で判断
        const res = await apiRequest("/api/v1/chat/", "GET");
        const currentChats = res.data.data as Chats[];
        const currentChatCount = currentChats.length;

        if (isFree && currentChatCount >= 2) {
          setUpgradeDialog(true);
        } else if (currentChatCount >= 20) {
          setOpenDeleteDialog(true);
        } else {
          executeNewChatCreation();
        }
        return;
      }

      // 制限情報を使用して判断
      if (limits.current_count >= limits.max_chats) {
        if (limits.is_free) {
          setUpgradeDialog(true);
        } else {
          setOpenDeleteDialog(true);
        }
      } else {
        // 制限内なら新しいチャットを作成
        executeNewChatCreation();
      }
    } catch (error) {
      console.error("Error in handleAddChat:", error);
      setAlert("error", t("common.error"));
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant={"h6"}
          component={"h3"}
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-start",
            mb: 2,
            width: "100%",
          }}
        >
          <ChatIcon sx={{ color: theme.palette.secondary.main, mr: 2 }} />
          {t("dashboard.search")}
        </Typography>
      </Box>
      <Grid container spacing={2}>
        {data?.chats?.map((ws, index) => {
          return (
            <Grid
              key={index}
              size={{
                xs: 6,
                sm: 4,
              }}
            >
              <ButtonBase
                onClick={() => navigate("/chat/" + ws.uuid)}
                sx={{
                  transition: "0.3s",
                  width: "100%",
                  "&:hover": {
                    transform: "translateY(-5px)",
                  },
                }}
              >
                <Grow in={true} timeout={(index + 1) * 500}>
                  <Paper
                    elevation={0}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: 150,
                      overflow: "hidden",
                      backdropFilter: "blur(8px)",
                      backgroundColor: (theme) => alpha(theme.palette.background.custom1, 0.25),
                      borderRadius: 2,
                    }}
                  >
                    <Box
                      sx={{
                        height: "5px",
                        width: "100%",
                        background: theme.custom.gradient.main,
                      }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        height: 140,
                        p: 1,
                        mt: 1,
                      }}
                    >
                      <Typography
                        variant={"subtitle2"}
                        component={"p"}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          mb: 0.5,
                          textAlign: "left",
                        }}
                      >
                        {ws.name}
                      </Typography>
                      <Typography
                        variant={"caption"}
                        component={"p"}
                        sx={{
                          mb: 0.5,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          textAlign: "left",
                        }}
                      >
                        {ws.latest_history?.replace(/#|\*|--|\|/g, "") ?? ""}
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      <Typography
                        variant={"caption"}
                        component={"p"}
                        sx={{
                          mb: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          textAlign: "right",
                        }}
                      >
                        {localDate(ws.updated_at, uiLanguage)}
                      </Typography>
                    </Box>
                  </Paper>
                </Grow>
              </ButtonBase>
            </Grid>
          );
        })}
        {data?.chats && data.chats.length < 10 && (
          <Grid
            size={{
              xs: 6,
              sm: 4,
            }}
          >
            <ButtonBase
              onClick={handleAddChat}
              sx={{
                transition: "0.3s",
                width: "100%",
                "&:hover": {
                  transform: "translateY(-5px)",
                },
              }}
            >
              <Grow in={true} timeout={500 * ((data?.chats?.length ?? 0) + 1)}>
                <Paper
                  elevation={0}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 150,
                    border: `2px dashed ${alpha(theme.palette.primary.main, 0.5)}`,
                    backdropFilter: "blur(8px)",
                    backgroundColor: (theme) => alpha(theme.palette.background.custom1, 0.25),
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                      alignItems: "center",
                      justifyContent: "center",
                      m: 1,
                    }}
                  >
                    <Typography variant={"subtitle1"} textAlign={"center"} component={"p"} sx={{ mb: 1 }}>
                      Add a new Chat
                    </Typography>
                    <AddIcon sx={{ mb: 1, color: theme.palette.primary.main }} />
                    <Typography variant={"caption"} component={"p"}>
                      {t("dashboard.addChat")}
                    </Typography>
                  </Box>
                </Paper>
              </Grow>
            </ButtonBase>
          </Grid>
        )}
      </Grid>
      <BrowsingDialogs
        openDeleteDialog={openDeleteDialog}
        upgradeDialog={upgradeDialog}
        handleCloseDialog={handleCloseDialog}
        handleConfirmDelete={handleConfirmDelete}
        handleUpgrade={handleUpgrade}
        handleCreateNewChat={handleCreateNewChat}
      />
    </Box>
  );
};
export default Browsing;
